import { useForm } from 'react-hook-form';

import { z } from 'zod';

import { PolicyGroupResponse } from '@/types/service/policyGroup';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { zodResolver } from '@hookform/resolvers/zod';

// Validation Schema
const PolicyGroupFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Group name is required')
    .max(255, 'Group name must be less than 255 characters'),
});

type PolicyGroupFormData = z.infer<typeof PolicyGroupFormSchema>;

// Create Policy Group Modal
interface CreatePolicyGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PolicyGroupFormData) => Promise<void>;
}

export const CreatePolicyGroupModal = ({
  open,
  onOpenChange,
  onSubmit,
}: CreatePolicyGroupModalProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<PolicyGroupFormData>({
    resolver: zodResolver(PolicyGroupFormSchema),
    defaultValues: {
      name: '',
    },
  });

  const handleFormSubmit = async (data: PolicyGroupFormData) => {
    try {
      await onSubmit(data);
      reset();
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component via toast
    }
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Create New Policy Group</DialogTitle>
          <DialogDescription>
            Create a new group to organize users for policy assignments.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='name'>
                Group Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='name'
                placeholder='e.g., Engineering Team'
                {...register('name')}
                disabled={isSubmitting}
              />
              {errors.name && <p className='text-sm text-destructive'>{errors.name.message}</p>}
            </div>
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Group'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Delete Policy Group Modal
interface DeletePolicyGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: PolicyGroupResponse | null;
  memberCount?: number;
  onConfirm: (groupId: number) => Promise<void>;
  isDeleting: boolean;
}

export const DeletePolicyGroupModal = ({
  open,
  onOpenChange,
  group,
  memberCount = 0,
  onConfirm,
  isDeleting,
}: DeletePolicyGroupModalProps) => {
  const handleConfirm = async () => {
    if (!group) return;

    try {
      await onConfirm(group.id);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component via toast
    }
  };

  if (!group) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Policy Group?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the group <strong>"{group.name}"</strong>?
            {memberCount > 0 && (
              <span className='block mt-2 text-amber-600 font-medium'>
                This group has {memberCount} {memberCount === 1 ? 'member' : 'members'}. They will
                be removed from the group.
              </span>
            )}
            <span className='block mt-2'>This action cannot be undone.</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDeleting}
            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
          >
            {isDeleting ? 'Deleting...' : 'Delete Group'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
